<template>
  <div
    class="native-scorecard-section py-3 px-3"
    :class="{
      'is-exporting': isExporting,
      'export-hide-right': isExporting && exportTarget === 'left',
      'export-hide-left': isExporting && exportTarget === 'right'
    }"
  >
    <!-- Print-only header -->
    <div class="print-header">
      <div class="print-title-area">
        <h3 class="print-main-title">{{ getExportTitle }}</h3>
        <p class="print-sub-title">Indicator: {{ selectedIndicatorFullName }}</p>
      </div>
      <div class="print-logo-area">
        <img src="https://i.imgur.com/yyqklZM.png" alt="MSDAT Logo" class="print-logo" />
      </div>
    </div>

    <!-- Header Dropdowns for Indicator and Source selection -->
    <div class="row align-items-center mb-4 pb-3 border-bottom">
      <div class="col-md-5 mb-2 mb-md-0">
        <label class="font-weight-bold text-muted small text-uppercase">Indicator</label>
        <b-dropdown
          :text="selectedIndicatorName"
          variant="outline-success"
          class="w-100 custom-premium-dropdown"
          menu-class="w-100"
        >
          <b-dropdown-item
            v-for="ind in filteredIndicators"
            :key="ind.id"
            :active="selectedIndicatorId === ind.id"
            @click="selectedIndicatorId = ind.id"
          >
            {{ ind.full_name || ind.short_name }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="col-md-3 mb-2 mb-md-0">
        <label class="font-weight-bold text-muted small text-uppercase">Non-Routine Source</label>
        <b-dropdown
          :text="selectedDatasourceName"
          variant="outline-success"
          class="w-100 custom-premium-dropdown"
          menu-class="w-100"
        >
          <b-dropdown-item
            v-for="ds in filteredDatasources"
            :key="ds.id"
            :active="selectedDatasourceId === ds.id"
            @click="selectedDatasourceId = ds.id"
          >
            {{ ds.datasource || ds.full_name }}
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <div class="col-md-3 d-flex justify-content-end align-items-end mt-3 mt-md-0">
        <!-- Legend -->
        <div class="legend-container">
          <div class="d-flex legend-bar align-items-center">
            <span class="mr-2 small font-weight-bold text-muted text-uppercase">Change:</span>
            <div class="legend-item legend-decrease">Decrease</div>
            <div class="legend-item legend-no-change">No Change</div>
            <div class="legend-item legend-increase">Increase</div>
          </div>
        </div>
      </div>

      <div class="col-md-1 d-flex justify-content-end align-items-end mt-3 mt-md-0">
        <b-dropdown
          variant="outline-success"
          size="sm"
          class="w-100 custom-premium-dropdown"
          menu-class="w-100"
          no-caret
          right
          data-html2canvas-ignore
        >
          <template #button-content>
            <div class="d-flex align-items-center justify-content-center w-100">
              <b-spinner v-if="pdfLoading" small class="mr-1"></b-spinner>
              <b-icon v-else icon="download" class="mr-1" />
              <span>Download</span>
            </div>
          </template>
          <b-dropdown-item :disabled="pdfLoading" @click="triggerDownload('all', 'pdf')">PDF</b-dropdown-item>
          <b-dropdown-item :disabled="pdfLoading" @click="triggerDownload('all', 'png')">PNG Image</b-dropdown-item>
          <b-dropdown-item :disabled="pdfLoading" @click="triggerDownload('all', 'svg')">SVG Image</b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading scorecard data...</span>
      </div>
      <p class="mt-2 text-muted font-weight-bold">Loading scorecard data...</p>
    </div>

    <!-- Main Content Grid -->
    <div v-else class="row no-gutters table-wrapper">
      <!-- LEFT TABLE (Non-routine Data) -->
      <div :class="[isTfr ? 'col-xl-5' : 'col-xl-6', 'col-12 pr-xl-2 mb-4']">
        <div class="card shadow-sm h-100 table-card">
          <div class="card-header bg-dark text-white py-2 d-flex justify-content-between align-items-center">
            <h6 class="font-weight-bold mb-0">Non-routine Data ({{ getDatasourceName(selectedDatasourceId) }})</h6>
            <b-dropdown size="sm" variant="link" toggle-class="text-white p-0" no-caret right data-html2canvas-ignore>
              <template #button-content>
                <b-icon icon="download" class="text-white" />
              </template>
              <b-dropdown-item @click="triggerDownload('left', 'pdf')">Download PDF</b-dropdown-item>
              <b-dropdown-item @click="triggerDownload('left', 'png')">Download PNG Image</b-dropdown-item>
              <b-dropdown-item @click="triggerDownload('left', 'svg')">Download SVG Image</b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="table-responsive">
            <!-- Case A: TFR + MICS (Static MICS Layout) -->
            <table v-if="isTfr && selectedDatasourceId === 1" class="table table-sm table-bordered mb-0 scorecard-table">
              <thead>
                <tr>
                  <th rowspan="2" class="align-middle text-left location-col">Location</th>
                  <th colspan="1" class="text-center period-header">2011</th>
                  <th colspan="2" class="text-center period-header">2016</th>
                  <th colspan="2" class="text-center period-header">2021</th>
                  <th rowspan="2" class="align-middle text-center trend-col">Trend</th>
                </tr>
                <tr>
                  <th class="text-center sub-col">Value</th>
                  <th class="text-center sub-col">Value</th>
                  <th class="text-center sub-col">% Change</th>
                  <th class="text-center sub-col">Value</th>
                  <th class="text-center sub-col">% Change</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="locName in sortedLocations" :key="locName">
                  <td class="text-left font-weight-bold location-cell">{{ locName }}</td>
                  <td class="text-center">{{ getStaticVal(locName, 'mics', '2011') }}</td>
                  <td class="text-center">{{ getStaticVal(locName, 'mics', '2016') }}</td>
                  <td class="text-center pct-cell" :style="getStaticPctStyle(locName, 'mics', '2011', '2016')">
                    {{ formatPct(getStaticPctChange(locName, 'mics', '2011', '2016')) }}
                  </td>
                  <td class="text-center">{{ getStaticVal(locName, 'mics', '2021') }}</td>
                  <td class="text-center pct-cell" :style="getStaticPctStyle(locName, 'mics', '2016', '2021')">
                    {{ formatPct(getStaticPctChange(locName, 'mics', '2016', '2021')) }}
                  </td>
                  <td class="text-center align-middle">
                    <Sparkline :dataPoints="getStaticSparkData(locName, 'mics')" />
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Case B: General Dynamic Non-routine Layout -->
            <table v-else-if="dynamicPeriods.length > 0" class="table table-sm table-bordered mb-0 scorecard-table">
              <thead>
                <tr>
                  <th rowspan="2" class="align-middle text-left location-col">Location</th>
                  <th v-for="(p, idx) in dynamicPeriods" :key="p" :colspan="idx === 0 ? 1 : 2" class="text-center period-header">
                    {{ p }}
                  </th>
                  <th rowspan="2" class="align-middle text-center trend-col">Trend</th>
                </tr>
                <tr>
                  <th v-for="h in dynamicHeaders" :key="h.key" class="text-center sub-col">
                    {{ h.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="loc in sortedStatesList" :key="loc.id">
                  <td class="text-left font-weight-bold location-cell">{{ loc.name }}</td>
                  <td v-for="cell in getDynamicRowCells(loc.id)" :key="cell.key"
                      :class="cell.class" :style="cell.style">
                    {{ cell.val }}
                  </td>
                  <td class="text-center align-middle">
                    <Sparkline :dataPoints="getDynamicSparkData(loc.id)" />
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-center py-5 text-muted">
              No data available in database for this indicator and source.
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT TABLE (Routine Data) -->
      <div :class="[isTfr ? 'col-xl-7' : 'col-xl-6', 'col-12 pl-xl-2 mb-4']">
        <div class="card shadow-sm h-100 table-card">
          <div class="card-header bg-dark text-white py-2 d-flex justify-content-between align-items-center">
            <h6 class="font-weight-bold mb-0">Routine Data (NHMIS-DHIS2)</h6>
            <b-dropdown size="sm" variant="link" toggle-class="text-white p-0" no-caret right data-html2canvas-ignore>
              <template #button-content>
                <b-icon icon="download" class="text-white" />
              </template>
              <b-dropdown-item @click="triggerDownload('right', 'pdf')">Download PDF</b-dropdown-item>
              <b-dropdown-item @click="triggerDownload('right', 'png')">Download PNG Image</b-dropdown-item>
              <b-dropdown-item @click="triggerDownload('right', 'svg')">Download SVG Image</b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="table-responsive">
            <!-- Case A: TFR (Static Quarterly Layout) -->
            <table v-if="isTfr" class="table table-sm table-bordered mb-0 scorecard-table">
              <thead>
                <tr>
                  <th rowspan="2" class="align-middle text-left location-col">Location</th>
                  <th v-for="q in staticQuarters" :key="q" colspan="2" class="text-center period-header-sm">
                    {{ q.replace('_', ' ') }}
                  </th>
                  <th rowspan="2" class="align-middle text-center trend-col">Trend</th>
                </tr>
                <tr>
                  <th v-for="h in staticHeaders" :key="h.key" class="text-center sub-col-sm">
                    {{ h.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="locName in sortedLocations" :key="locName">
                  <td class="text-left font-weight-bold location-cell">{{ locName }}</td>
                  <td v-for="cell in getStaticRowCells(locName)" :key="cell.key"
                      :class="cell.class" :style="cell.style">
                    {{ cell.val }}
                  </td>
                  <td class="text-center align-middle">
                    <Sparkline :dataPoints="getStaticSparkData(locName, 'nhmis')" />
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Case B: General Dynamic Routine Layout -->
            <table v-else-if="routinePeriods.length > 0" class="table table-sm table-bordered mb-0 scorecard-table">
              <thead>
                <tr>
                  <th rowspan="2" class="align-middle text-left location-col">Location</th>
                  <th v-for="(p, idx) in routinePeriods" :key="p" :colspan="idx === 0 ? 1 : 2" class="text-center period-header">
                    {{ p }}
                  </th>
                  <th rowspan="2" class="align-middle text-center trend-col">Trend</th>
                </tr>
                <tr>
                  <th v-for="h in routineHeaders" :key="h.key" class="text-center sub-col">
                    {{ h.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="loc in sortedStatesList" :key="loc.id">
                  <td class="text-left font-weight-bold location-cell">{{ loc.name }}</td>
                  <td v-for="cell in getRoutineRowCells(loc.id)" :key="cell.key"
                      :class="cell.class" :style="cell.style">
                    {{ cell.val }}
                  </td>
                  <td class="text-center align-middle">
                    <Sparkline :dataPoints="getRoutineSparkData(loc.id)" />
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-center py-5 text-muted">
              No routine data available for this indicator.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Print-only footer -->
    <div class="print-footer">
      <div class="print-source">
        Source: MSDAT
      </div>
      <div class="print-date">
        Generated on: {{ new Date().toLocaleDateString() }}
      </div>
    </div>
  </div>
</template>

<script>
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ApiServices from '@/modules/data-layer/services/ApiServices';
import Sparkline from './Sparkline.vue';
import staticData from './scorecard-demographics-data.json';

export default {
  name: 'NativeScorecardSection',
  components: { Sparkline },
  data() {
    return {
      selectedIndicatorId: 1, // Default: Total Fertility Rate
      selectedDatasourceId: 1, // Default: MICS
      staticScorecard: staticData,
      staticQuarters: [
        'Q1_2022', 'Q2_2022', 'Q3_2022', 'Q4_2022',
        'Q1_2023', 'Q2_2023', 'Q3_2023', 'Q4_2023',
      ],
      loading: false,
      pdfLoading: false,
      isExporting: false,
      exportTarget: 'all',
      availableDatasources: [], // Dynamically loaded from API
      // Dynamic arrays populated from API
      dynamicResults: [],
      routineResults: [],
      dynamicPeriods: [],
      routinePeriods: [],
      // Maps to easily query dynamic values
      dynamicMap: {},
      routineMap: {},
    };
  },
  computed: {
    configObject() {
      return this.$store.getters['MSDAT_STORE/getConfigObject'] || {};
    },
    // Filter indicators listed in the demographics dashboard config
    filteredIndicators() {
      const allowedIds = this.configObject.indicators || [];
      const globalIndicators = this.$store.state.DL?.indicators || [];

      // Filter indicators. To prevent duplicate "Total fertility rate",
      // we exclude ID 63 and keep ID 1 (which maps to all surveys & routine data).
      return globalIndicators.filter((ind) => allowedIds.includes(ind.id) && ind.id !== 63);
    },
    // Filter datasources dynamically fetched for this indicator, excluding Routine classification
    filteredDatasources() {
      return this.availableDatasources.filter((ds) => ds.classification !== 'Routine');
    },
    // Checks if the user has selected TFR (ID 1) and MICS (ID 1)
    isTfr() {
      return this.selectedIndicatorId === 1 || this.selectedIndicatorId === 63;
    },
    sortedLocations() {
      return [
        'Nigeria', 'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa',
        'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu',
        'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi',
        'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun',
        'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara',
      ];
    },
    // List of locations from store filtered and sorted for the general scorecard
    sortedStatesList() {
      const list = this.$store.state.DL?.location || [];
      const states = list.filter((l) => l.level === 3 || l.id === 1); // States + Nigeria National
      return [...states].sort((a, b) => {
        if (a.id === 1) return -1;
        if (b.id === 1) return 1;
        return a.name.localeCompare(b.name);
      });
    },
    staticHeaders() {
      const headers = [];
      this.staticQuarters.forEach((q) => {
        headers.push({ key: `${q}-val`, label: 'Val' });
        headers.push({ key: `${q}-pct`, label: '%' });
      });
      return headers;
    },
    dynamicHeaders() {
      const headers = [];
      this.dynamicPeriods.forEach((p, idx) => {
        headers.push({ key: `${p}-v`, label: 'Value' });
        if (idx > 0) {
          headers.push({ key: `${p}-p`, label: '% Change' });
        }
      });
      return headers;
    },
    routineHeaders() {
      const headers = [];
      this.routinePeriods.forEach((p, idx) => {
        headers.push({ key: `${p}-v`, label: 'Value' });
        if (idx > 0) {
          headers.push({ key: `${p}-p`, label: '% Change' });
        }
      });
      return headers;
    },
    selectedIndicatorName() {
      const ind = this.filteredIndicators.find((i) => i.id === this.selectedIndicatorId);
      return ind ? (ind.full_name || ind.short_name) : '';
    },
    selectedDatasourceName() {
      const ds = this.filteredDatasources.find((d) => d.id === this.selectedDatasourceId);
      return ds ? (ds.datasource || ds.full_name) : '';
    },
    selectedIndicatorFullName() {
      const indicator = this.filteredIndicators.find((ind) => ind.id === this.selectedIndicatorId);
      return indicator ? (indicator.full_name || indicator.short_name) : '';
    },
    getExportTitle() {
      const indicatorName = this.selectedIndicatorFullName;
      const sourceName = this.selectedDatasourceName;
      if (this.exportTarget === 'left') {
        return `Non-Routine Data (${sourceName}) - ${indicatorName}`;
      } if (this.exportTarget === 'right') {
        return `Routine Data (NHMIS-DHIS2) - ${indicatorName}`;
      }
      return `Demographics Scorecard - ${indicatorName}`;
    },
  },
  watch: {
    selectedIndicatorId: {
      immediate: true,
      async handler(newId) {
        // Map TFR Estimates (ID 63) to TFR Surveys (ID 1) dynamically to unify filtering
        if (newId === 63) {
          this.selectedIndicatorId = 1;
          return;
        }

        this.loading = true;
        await this.fetchAvailableDatasources();

        const available = this.filteredDatasources;
        if (available.length > 0) {
          const isStillAvailable = available.some((ds) => ds.id === this.selectedDatasourceId);
          if (!isStillAvailable) {
            this.selectedDatasourceId = available[0].id;
            // Since selectedDatasourceId watcher will trigger fetchDynamicScorecardData,
            // we do NOT need to call it here to avoid duplicate API requests.
            return;
          }
        }
        this.fetchDynamicScorecardData();
      },
    },
    selectedDatasourceId() {
      this.fetchDynamicScorecardData();
    },
  },
  methods: {
    async fetchAvailableDatasources() {
      try {
        const res = await ApiServices.getIndicatorDataSources(this.selectedIndicatorId);
        this.availableDatasources = res?.data?.datasources || [];
      } catch (err) {
        console.error('Failed to load datasources for indicator:', err);
        this.availableDatasources = [];
      }
    },
    getDatasourceName(id) {
      const ds = (this.$store.state.DL?.datasources || []).find((d) => d.id === id);
      return ds ? (ds.datasource || ds.full_name) : `Source ${id}`;
    },
    // Fetch dynamic scorecard values from the server
    async fetchDynamicScorecardData() {
      if (this.isTfr && this.selectedDatasourceId === 1) {
        this.loading = false;
        return; // Left and Right tables are static MICS/NHMIS TFR layout
      }

      this.loading = true;
      try {
        if (this.isTfr) {
          // TFR only queries dynamic survey source for Left Table (Right Table remains quarterly static)
          const surveyRes = await ApiServices.getDataObj({
            indicatorID: this.selectedIndicatorId,
            datasourceID: this.selectedDatasourceId,
          });
          const surveyData = surveyRes?.data?.results || [];
          this.processDynamicData(surveyData, 'dynamic');
        } else {
          // Other indicators query both left (survey/estimate) and right (routine) dynamic endpoints
          const surveyRes = await ApiServices.getDataObj({
            indicatorID: this.selectedIndicatorId,
            datasourceID: this.selectedDatasourceId,
          });
          const surveyData = surveyRes?.data?.results || [];
          this.processDynamicData(surveyData, 'dynamic');

          const routineRes = await ApiServices.getDataObj({
            indicatorID: this.selectedIndicatorId,
            datasourceID: 6,
          });
          const routineData = routineRes?.data?.results || [];
          this.processDynamicData(routineData, 'routine');
        }
      } catch (err) {
        console.error('Failed to load dynamic scorecard details:', err);
      } finally {
        this.loading = false;
      }
    },
    // Map data points by location and period
    processDynamicData(data, type) {
      const map = {};
      const periodsSet = new Set();

      data.forEach((item) => {
        if (item.period && item.location) {
          const val = parseFloat(item.value);
          if (!Number.isNaN(val)) {
            map[`${item.location}_${item.period}`] = val;
            periodsSet.add(item.period);
          }
        }
      });

      // Sort and keep the last 4 periods (for columns width/readability)
      const sortedPeriods = Array.from(periodsSet).sort().slice(-4);

      if (type === 'dynamic') {
        this.dynamicMap = map;
        this.dynamicPeriods = sortedPeriods;
        this.dynamicResults = data;
      } else {
        this.routineMap = map;
        this.routinePeriods = sortedPeriods;
        this.routineResults = data;
      }
    },
    getStaticRowCells(locName) {
      const cells = [];
      this.staticQuarters.forEach((q, idx) => {
        cells.push({
          key: `${q}-val`,
          val: this.getStaticVal(locName, 'nhmis', q),
          class: 'text-center',
        });
        cells.push({
          key: `${q}-pct`,
          val: idx > 0 ? this.formatPct(this.getStaticPctChange(locName, 'nhmis', this.staticQuarters[idx - 1], q)) : '-',
          class: 'text-center pct-cell',
          style: idx > 0 ? this.getStaticPctStyle(locName, 'nhmis', this.staticQuarters[idx - 1], q) : {},
        });
      });
      return cells;
    },
    getDynamicRowCells(locId) {
      const cells = [];
      this.dynamicPeriods.forEach((p, idx) => {
        cells.push({
          key: `${p}-v`,
          val: this.getDynamicVal(locId, p),
          class: 'text-center',
        });
        if (idx > 0) {
          cells.push({
            key: `${p}-p`,
            val: this.formatPct(this.getDynamicPctChange(locId, this.dynamicPeriods[idx - 1], p)),
            class: 'text-center pct-cell',
            style: this.getDynamicPctStyle(locId, this.dynamicPeriods[idx - 1], p),
          });
        }
      });
      return cells;
    },
    getRoutineRowCells(locId) {
      const cells = [];
      this.routinePeriods.forEach((p, idx) => {
        cells.push({
          key: `${p}-v`,
          val: this.getRoutineVal(locId, p),
          class: 'text-center',
        });
        if (idx > 0) {
          cells.push({
            key: `${p}-p`,
            val: this.formatPct(this.getRoutinePctChange(locId, this.routinePeriods[idx - 1], p)),
            class: 'text-center pct-cell',
            style: this.getRoutinePctStyle(locId, this.routinePeriods[idx - 1], p),
          });
        }
      });
      return cells;
    },

    // --- Static Scorecard Helpers ---
    getStaticVal(location, source, period) {
      return this.staticScorecard[location]?.[source]?.[period] ?? '-';
    },
    getStaticPctChange(location, source, prevPeriod, currPeriod) {
      if (!prevPeriod) return null;
      const prevVal = this.staticScorecard[location]?.[source]?.[prevPeriod];
      const currVal = this.staticScorecard[location]?.[source]?.[currPeriod];

      if (prevVal === undefined || currVal === undefined || prevVal === 0) return null;
      return ((currVal - prevVal) / prevVal) * 100;
    },
    getStaticPctStyle(location, source, prevPeriod, currPeriod) {
      const pct = this.getStaticPctChange(location, source, prevPeriod, currPeriod);
      return this.getColorStyle(pct);
    },
    getStaticSparkData(location, source) {
      const dataObj = this.staticScorecard[location]?.[source];
      if (!dataObj) return [];

      if (source === 'mics') {
        return ['2011', '2016', '2021'].map((p) => dataObj[p]).filter((val) => val !== undefined);
      }
      return this.staticQuarters.map((p) => dataObj[p]).filter((val) => val !== undefined);
    },

    // --- Dynamic Scorecard Helpers ---
    getDynamicVal(locationId, period) {
      return this.dynamicMap[`${locationId}_${period}`] ?? '-';
    },
    getDynamicPctChange(locationId, prevPeriod, currPeriod) {
      const prevVal = this.dynamicMap[`${locationId}_${prevPeriod}`];
      const currVal = this.dynamicMap[`${locationId}_${currPeriod}`];
      if (prevVal === undefined || currVal === undefined || prevVal === 0) return null;
      return ((currVal - prevVal) / prevVal) * 100;
    },
    getDynamicPctStyle(locationId, prevPeriod, currPeriod) {
      const pct = this.getDynamicPctChange(locationId, prevPeriod, currPeriod);
      return this.getColorStyle(pct);
    },
    getDynamicSparkData(locationId) {
      return this.dynamicPeriods
        .map((p) => this.dynamicMap[`${locationId}_${p}`])
        .filter((val) => val !== undefined);
    },

    getRoutineVal(locationId, period) {
      return this.routineMap[`${locationId}_${period}`] ?? '-';
    },
    getRoutinePctChange(locationId, prevPeriod, currPeriod) {
      const prevVal = this.routineMap[`${locationId}_${prevPeriod}`];
      const currVal = this.routineMap[`${locationId}_${currPeriod}`];
      if (prevVal === undefined || currVal === undefined || prevVal === 0) return null;
      return ((currVal - prevVal) / prevVal) * 100;
    },
    getRoutinePctStyle(locationId, prevPeriod, currPeriod) {
      const pct = this.getRoutinePctChange(locationId, prevPeriod, currPeriod);
      return this.getColorStyle(pct);
    },
    getRoutineSparkData(locationId) {
      return this.routinePeriods
        .map((p) => this.routineMap[`${locationId}_${p}`])
        .filter((val) => val !== undefined);
    },

    // --- Shared Helper Methods ---
    formatPct(val) {
      if (val === null || val === undefined) return '-';
      const formatted = val.toFixed(1);
      return val > 0 ? `+${formatted}%` : `${formatted}%`;
    },
    getColorStyle(pct) {
      if (pct === null || pct === undefined) return {};
      const intensity = Math.min(Math.abs(pct) / 30, 1);

      if (pct < 0) {
        // Decrease: Blue background
        return {
          backgroundColor: `rgba(0, 102, 204, ${0.15 + intensity * 0.75})`,
          color: intensity > 0.6 ? '#ffffff' : '#000000',
        };
      } if (pct > 0) {
        // Increase: Green background
        return {
          backgroundColor: `rgba(40, 167, 69, ${0.15 + intensity * 0.75})`,
          color: intensity > 0.6 ? '#ffffff' : '#000000',
        };
      }
      return {
        backgroundColor: '#e9ecef',
        color: '#495057',
      };
    },
    async triggerDownload(target, format) {
      this.pdfLoading = true;
      this.isExporting = true;
      this.exportTarget = target;

      await this.$nextTick();

      try {
        const element = this.$el;
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/png');

        // Dynamic filename
        const indicator = this.filteredIndicators.find((ind) => ind.id === this.selectedIndicatorId);
        const indicatorName = indicator ? (indicator.full_name || indicator.short_name) : 'Scorecard';
        const sourceName = this.getDatasourceName(this.selectedDatasourceId);

        let targetLabel = '';
        if (target === 'left') {
          targetLabel = `Non_Routine_Data_${sourceName}`;
        } else if (target === 'right') {
          targetLabel = 'Routine_Data_NHMIS';
        } else {
          targetLabel = `Demographics_Scorecard_${sourceName}`;
        }

        const baseFilename = `${indicatorName.replace(/\s+/g, '_')}_${targetLabel.replace(/\s+/g, '_')}`;

        if (format === 'pdf') {
          // eslint-disable-next-line new-cap
          const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'pt',
            format: 'a4',
          });

          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();

          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;

          // Calculate size to fit scorecard perfectly on a single landscape page
          const margin = 20;
          const maxWidth = pdfWidth - (margin * 2);
          const maxHeight = pdfHeight - (margin * 2);

          let finalWidth = maxWidth;
          let finalHeight = (canvasHeight * finalWidth) / canvasWidth;

          // Scale down vertically if height exceeds available landscape height
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = (canvasWidth * finalHeight) / canvasHeight;
          }

          // Center horizontally and vertically
          const xOffset = margin + (maxWidth - finalWidth) / 2;
          const yOffset = margin + (maxHeight - finalHeight) / 2;

          pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalWidth, finalHeight);
          pdf.save(`${baseFilename}.pdf`);
        } else if (format === 'png') {
          const link = document.createElement('a');
          link.href = imgData;
          link.download = `${baseFilename}.png`;
          link.click();
        } else if (format === 'svg') {
          const svgContent = `
            <svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}">
              <image href="${imgData}" width="100%" height="100%"/>
            </svg>
          `;
          const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `${baseFilename}.svg`;
          link.click();
        }
      } catch (err) {
        console.error('Failed to trigger download:', err);
      } finally {
        this.pdfLoading = false;
        this.isExporting = false;
        this.exportTarget = 'all';
      }
    },
  },
};
</script>

<style scoped lang="scss">
.native-scorecard-section {
  font-family: 'Work Sans', sans-serif;
  background-color: #fcfcfc;
  border-radius: 8px;

  .custom-premium-dropdown {
    width: 100%;

    ::v-deep .btn {
      background-color: #ffffff !important;
      color: #334155 !important;
      border: 1px solid #007d53 !important;
      border-radius: 6px !important;
      height: 38px !important;
      padding: 0.375rem 0.75rem !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      text-align: left !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;

      &:focus, &:active, &.active {
        border-color: #007d53 !important;
        box-shadow: 0 0 0 0.2rem rgba(0, 125, 83, 0.15) !important;
        outline: 0 !important;
      }
    }

    ::v-deep .dropdown-menu {
      border-radius: 6px !important;
      border: 1px solid #cbd5e1 !important;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
      max-height: 300px !important;
      overflow-y: auto !important;
      padding: 4px 0 !important;
      width: 100% !important;
      max-width: 100% !important;
    }

    ::v-deep .dropdown-item {
      color: #334155 !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      padding: 8px 16px !important;
      transition: background-color 0.15s ease-in-out !important;
      white-space: normal !important;

      &:hover {
        background-color: rgba(0, 125, 83, 0.1) !important;
        color: #334155 !important;
      }

      &.active, &:active {
        background-color: #007d53 !important;
        color: #ffffff !important;
      }
    }
  }

  .custom-pdf-btn {
    background-color: #ffffff !important;
    color: #334155 !important;
    border: 1px solid #007d53 !important;
    border-radius: 6px !important;
    font-size: 14px !important;
    font-weight: 600 !important;

    &:hover {
      background-color: rgba(0, 125, 83, 0.05) !important;
      color: #007d53 !important;
      border-color: #007d53 !important;
    }

    &:focus, &:active, &.active {
      background-color: #ffffff !important;
      color: #334155 !important;
      border-color: #007d53 !important;
      box-shadow: 0 0 0 0.2rem rgba(0, 125, 83, 0.15) !important;
      outline: 0 !important;
    }
  }

  .legend-bar {
    border-radius: 4px;
    overflow: hidden;
    font-size: 11px;
    font-weight: 600;
  }

  .legend-item {
    padding: 2px 10px;
    text-align: center;
    color: white;
  }

  .legend-decrease { background-color: rgba(0, 102, 204, 0.85); }
  .legend-no-change { background-color: #e9ecef; color: #495057; }
  .legend-increase { background-color: rgba(40, 167, 69, 0.85); }

  .table-wrapper {
    margin: 0 -5px;
  }

  .table-card {
    border: 0;
    border-radius: 6px;
    overflow: hidden;
  }

  .scorecard-table {
    font-size: 12px;
    border-collapse: collapse;

    th {
      font-weight: 700;
      color: #333;
      background-color: #f8f9fa;
      border-color: #dee2e6;
      text-transform: uppercase;
      font-size: 10px;
    }

    .period-header { font-size: 11px; font-weight: bold; }
    .period-header-sm { font-size: 9px; font-weight: bold; padding: 2px; }

    .sub-col { width: 50px; font-size: 9px; font-weight: normal; }
    .sub-col-sm { width: 35px; font-size: 8px; font-weight: normal; padding: 1px; }

    .location-col { width: 100px; }
    .trend-col { width: 60px; }

    td {
      padding: 4px 6px;
      vertical-align: middle;
      height: 30px;
      border-color: #e9ecef;
    }

    .location-cell {
      background-color: #ffffff;
      color: #212529;
      white-space: nowrap;
    }

    .pct-cell {
      font-weight: 600;
      transition: background-color 0.15s ease;

      &:hover {
        filter: brightness(0.9);
      }
    }
  }

  /* Export and Print Styling */
  .print-header {
    display: none;
    border-bottom: 2px solid #007d53;
    padding-bottom: 12px;
    margin-bottom: 20px;
    width: 100%;

    .print-main-title {
      font-size: 20px;
      font-weight: 700;
      color: #007d53;
      margin: 0 0 4px 0;
    }

    .print-sub-title {
      font-size: 13px;
      color: #475569;
      margin: 0;
      font-weight: 600;
    }

    .print-logo {
      height: 28px;
      width: auto;
      object-fit: contain;
    }
  }

  .print-footer {
    display: none;
    border-top: 1px solid #cbd5e1;
    padding-top: 12px;
    margin-top: 20px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.4);
    font-weight: 500;
    width: 100%;
  }

  &.is-exporting {
    background-color: #ffffff !important;
    padding: 24px !important;

    /* Hide top controls row */
    .row.align-items-center.mb-4.pb-3.border-bottom {
      display: none !important;
    }

    .print-header {
      display: flex !important;
      align-items: center;
      justify-content: space-between;
    }

    .print-footer {
      display: flex !important;
      align-items: center;
      justify-content: space-between;
    }

    /* Column Width adjustments during export */
    &.export-hide-right {
      .col-12.pr-xl-2 {
        flex: 0 0 100% !important;
        max-width: 100% !important;
        padding-right: 0 !important;
      }
      .col-12.pl-xl-2 {
        display: none !important;
      }
    }

    &.export-hide-left {
      .col-12.pl-xl-2 {
        flex: 0 0 100% !important;
        max-width: 100% !important;
        padding-left: 0 !important;
      }
      .col-12.pr-xl-2 {
        display: none !important;
      }
    }
  }
}
</style>
